"use client";

/**
 * OrdersTableWidget — recent orders with full ID tooltip and side chips.
 */
import React, { useMemo } from "react";
import { Skeleton, Tooltip } from "antd";
import { HistoryOutlined, ReloadOutlined } from "@ant-design/icons";
import { DeskButton, DeskSideChip } from "@/shared/components/desk";
import { EnhancedTable } from "@/shared/components/tables/EnhancedTable";
import { formatCurrency } from "@/shared/utils/formatters";
import { formatQty } from "./lib/deskFormat";
import styles from "./WidgetChrome.module.css";
import local from "./TableWidgets.module.css";

export interface OrdersTableWidgetProps {
  orders: Record<string, unknown>[] | null | undefined;
  loading?: boolean;
  onNavigate: (href: string) => void;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  emptyTradeHref?: string;
  className?: string;
  /** Compact account-book columns for the dashboard side pane. */
  compact?: boolean;
}

function orderStatusClass(s: string): string {
  const key = (s || "").toLowerCase();
  if (key === "filled") return styles.chipFilled ?? "";
  if (key === "pending" || key === "open" || key === "new" || key === "partial") {
    return styles.chipPending ?? "";
  }
  return styles.chipDefault ?? "";
}

function orderRowKey(row: Record<string, unknown>): string {
  return String(
    row.id ??
    [
      row.symbol,
      row.created_at ?? row.createdAt ?? row.timestamp,
      row.side,
      row.quantity,
      row.price,
    ].join(":")
  );
}

function orderAgentId(row: Record<string, unknown>): string {
  for (const key of ["agent_id", "agentId"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function compactAgentId(value: string, compact: boolean): string {
  const maxLength = compact ? 10 : 14;
  if (value.length <= maxLength) return value;
  return compact
    ? `${value.slice(0, 4)}…${value.slice(-3)}`
    : `${value.slice(0, 7)}…${value.slice(-5)}`;
}

export function OrdersTableWidget({
  orders,
  loading = false,
  onNavigate,
  onRefresh,
  refreshDisabled = false,
  emptyTradeHref = "/trading/execution",
  className = "",
  compact = false,
}: OrdersTableWidgetProps) {
  const list = orders ?? [];
  const ready = orders != null;
  const count = list.length;
  const hasAgentAttribution = useMemo(
    () => list.some((row) => Boolean(orderAgentId(row))),
    [list]
  );
  const compactTableWidth = hasAgentAttribution ? 328 : 330;

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: "Order",
        dataIndex: "id",
        key: "id",
        width: 96,
        render: (id: string) => {
          if (!id) return <span className={styles.muted}>—</span>;
          const short = id.length > 8 ? `${id.slice(0, 8)}…` : id;
          return (
            <Tooltip title={id}>
              <code className={styles.mono} title={id}>
                {short}
              </code>
            </Tooltip>
          );
        },
      },
      {
        title: "Symbol",
        dataIndex: "symbol",
        key: "symbol",
        width: 88,
        render: (s: string) => (
          <span className={styles.cellSymbol}>{s || "—"}</span>
        ),
      },
      {
        title: "Side",
        dataIndex: "side",
        key: "side",
        width: 72,
        render: (s: string) => {
          return <DeskSideChip side={s} />;
        },
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 72,
        render: (t: string) => (
          <span className={styles.muted}>{(t || "—").toUpperCase()}</span>
        ),
      },
      {
        title: "Qty",
        dataIndex: "quantity",
        key: "quantity",
        align: "right" as const,
        width: 80,
        render: (q: number) => (
          <span className={`${styles.tabular} ${styles.cellNum}`}>
            {formatQty(q)}
          </span>
        ),
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        align: "right" as const,
        width: 96,
        render: (p: number) => (
          <span className={`${styles.tabular} ${styles.cellNum}`}>
            {p != null ? formatCurrency(p) : "Mkt"}
          </span>
        ),
      },
      ...(hasAgentAttribution
        ? [
          {
            title: "Agent",
            key: "agent",
            width: 112,
            render: (_: unknown, row: Record<string, unknown>) => {
              const agentId = orderAgentId(row);
              if (!agentId) return <span className={styles.muted}>—</span>;
              return (
                <Tooltip title={agentId}>
                  <code className={styles.mono} title={agentId}>
                    {compactAgentId(agentId, compact)}
                  </code>
                </Tooltip>
              );
            },
          },
        ]
        : []),
      {
        title: "State",
        dataIndex: "status",
        key: "status",
        width: 88,
        render: (s: string) => (
          <span className={orderStatusClass(s)}>{(s || "—").toUpperCase()}</span>
        ),
      },
    ];

    if (!compact) return allColumns;

    const compactWidths: Record<string, number> = hasAgentAttribution
      ? {
        symbol: 60,
        side: 56,
        quantity: 52,
        agent: 84,
        status: 76,
      }
      : {
        symbol: 66,
        side: 60,
        quantity: 56,
        price: 72,
        status: 76,
      };
    const visible = new Set(Object.keys(compactWidths));

    return allColumns
      .filter((column) => visible.has(String(column.key)))
      .map((column) => ({
        ...column,
        width: compactWidths[String(column.key)] ?? column.width,
      }));
  }, [compact, hasAgentAttribution]);

  if (loading) {
    return (
      <div className={className}>
        <Skeleton active paragraph={{ rows: 5 }} />
      </div>
    );
  }

  if (!ready || count === 0) {
    return (
      <div className={`${local.empty} ${className}`.trim()}>
        <div className={local.emptyIcon} aria-hidden>
          <HistoryOutlined />
        </div>
        <p className={local.emptyTitle}>
          {!ready ? "Waiting for orders" : "No recent orders"}
        </p>
        <p className={local.emptyDesc}>
          {!ready
            ? "Orders load when the trading connection is ready."
            : "Submitted and filled orders list here with side, size, and status."}
        </p>
        <div className={local.emptyActions}>
          <DeskButton type="primary" onClick={() => onNavigate(emptyTradeHref)}>
            Place an order
          </DeskButton>
          {onRefresh ? (
            <DeskButton
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              disabled={refreshDisabled}
            >
              Refresh
            </DeskButton>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.tableWrap} ${className}`.trim()}>
      <EnhancedTable
        dataSource={list}
        columns={columns}
        pagination={false}
        showToolbar={false}
        zebraStriped={false}
        density="dense"
        rowKey={(row) => orderRowKey(row as Record<string, unknown>)}
        size="small"
        scroll={{ x: compact ? compactTableWidth : 720 }}
      />
    </div>
  );
}
